(() => {
  const DEBOUNCE_MS = 300;
  const PREDICTIVE_TYPES = 'product,collection,query,page,article';
  const PREDICTIVE_LIMIT = 10;

  function debounce(fn, ms) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-predictive-search-form]');
      this.input = root.querySelector('[data-predictive-search-input]');
      this.clearBtn = root.querySelector('[data-predictive-search-clear]');
      this.closeBtn = root.querySelector('[data-predictive-search-close]');
      this.defaultView = root.querySelector('[data-predictive-search-default]');
      this.resultsView = root.querySelector('[data-predictive-search-results]');
      this.loading = root.querySelector('[data-predictive-search-loading]');

      if (!this.input) return;

      this.abortController = null;
      this.cache = new Map();

      this.onInput = debounce(this.handleInput.bind(this), DEBOUNCE_MS);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.handleFocus.bind(this));

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.clearBtn?.addEventListener('click', this.handleClear.bind(this));
      this.closeBtn?.addEventListener('click', this.handleClose.bind(this));

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.handleClose();
      });
    }

    handleFocus() {
      this.root.classList.add('is-active');
    }

    handleInput(event) {
      const query = event.target.value.trim();
      this.toggleClear(query.length > 0);

      if (!query) {
        this.showDefault();
        return;
      }

      this.showLoading();

      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }

      this.fetchResults(query);
    }

    async fetchResults(query) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const base = this.form?.action || '/search';
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('section_id', 'predictive-search');
      params.set('resources[type]', PREDICTIVE_TYPES);
      params.set('resources[limit]', PREDICTIVE_LIMIT);
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,tag,vendor,variants.title');
      const url = `${base}/suggest?${params.toString()}`;

      try {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(response.statusText);
        const html = await response.text();
        this.cache.set(query, html);
        this.render(html);
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.resultsView.innerHTML = `<p class="predictive-search__no-results">${this.errorMessage()}</p>`;
          this.showResults();
        }
      } finally {
        this.hideLoading();
      }
    }

    render(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newResults = doc.querySelector('[data-predictive-search-results]');

      if (newResults) {
        this.resultsView.innerHTML = newResults.innerHTML;
      } else if (doc.body) {
        this.resultsView.innerHTML = doc.body.innerHTML;
      } else {
        this.resultsView.innerHTML = `<p class="predictive-search__no-results">${this.errorMessage()}</p>`;
      }

      this.showResults();
    }

    showDefault() {
      this.defaultView?.removeAttribute('hidden');
      this.resultsView?.setAttribute('hidden', '');
      this.hideLoading();
    }

    showResults() {
      this.defaultView?.setAttribute('hidden', '');
      this.resultsView?.removeAttribute('hidden');
      this.hideLoading();
    }

    showLoading() {
      this.loading?.removeAttribute('hidden');
      this.defaultView?.setAttribute('hidden', '');
      this.resultsView?.setAttribute('hidden', '');
    }

    hideLoading() {
      this.loading?.setAttribute('hidden', '');
    }

    toggleClear(show) {
      if (!this.clearBtn) return;
      const wrap = this.input?.closest('.predictive-search__input-wrap');
      if (show) {
        this.clearBtn.removeAttribute('hidden');
        wrap?.classList.add('has-clear');
      } else {
        this.clearBtn.setAttribute('hidden', '');
        wrap?.classList.remove('has-clear');
      }
    }

    handleClear(event) {
      event?.preventDefault();
      this.input.value = '';
      this.input.focus();
      this.toggleClear(false);
      this.showDefault();
    }

    handleSubmit(event) {
      if (!this.input.value.trim()) event.preventDefault();
    }

    handleClose() {
      this.closePanel();
      this.input.value = '';
      this.toggleClear(false);
      this.showDefault();
    }

    closePanel() {
      const panel = this.root.closest('[data-search-panel]');
      const modal = this.root.closest('dialog-modal');
      const toggle = document.querySelector('[data-search-toggle]');
      const modalToggle = document.querySelector('[data-search-modal-toggle]');

      if (panel) {
        panel.setAttribute('hidden', '');
        toggle?.setAttribute('aria-expanded', 'false');
      } else if (modal && typeof modal.close === 'function') {
        modal.close();
        modalToggle?.setAttribute('aria-expanded', 'false');
      }

      document.body.classList.remove('modal-open');
    }

    errorMessage() {
      return this.root.dataset.errorMessage || window.themeStrings.noMatchingProducts;
    }
  }

  document.querySelectorAll('[data-predictive-search]').forEach((el) => {
    new PredictiveSearch(el);
  });
})();
